import { createSubscriptionManager } from './subscriptions';
import { createGatewayConnection } from './gateway';

// Explicit start only. Construction alone performs no network or refresh activity.
export function createRealtimeRuntime({ config, coordinator, gateway, getToken,
  createConnection = createGatewayConnection, onStatus = () => {}, retryMs = 10000 }) {
  let active = false;
  let disposed = false;
  let attempt;
  let timer;
  let subscriptions;
  let desired = [];
  let generation = 0;
  let connecting = false;
  const status = value => { if (active && !disposed) onStatus(value); };
  async function cleanup(current) {
    if (!current) return;
    current.retired = true;
    if (!current.stopping) current.stopping = Promise.resolve().then(() => current.connection?.stop()).catch(() => {});
    await current.stopping;
    if (current.sessionId && !current.sessionRemoved) {
      current.sessionRemoved = true;
      Promise.resolve().then(() => gateway.removeSession(current.sessionId)).catch(() => {});
    }
  }
  function retry() {
    if (!active || disposed) return;
    clearTimeout(timer); status('reconnecting');
    timer = setTimeout(() => { void connect(); }, retryMs);
  }
  async function connect() {
    if (!active || disposed || connecting) return;
    connecting = true;
    clearTimeout(timer);
    const id = ++generation;
    const old = attempt;
    subscriptions?.dispose(); subscriptions = undefined;
    await cleanup(old);
    if (!active || disposed || id !== generation) { connecting = false; return; }
    const current = { retired: false, sessionId: null, httpStatus: 0 };
    attempt = current;
    const valid = () => active && !disposed && generation === id && !current.retired;
    status('connecting');
    try {
      current.connection = createConnection({ config, getToken: async () => {
        try { return await getToken(); } catch (error) { current.httpStatus = error.status || 401; throw error; }
      },
        onSession: sessionId => {
          current.sessionId = sessionId;
          if (!valid()) void cleanup(current);
        },
        onHttpError: code => { current.httpStatus = code; } });
      current.connection.on('DomainChanged', event => { if (valid()) coordinator.receive(event); });
      current.connection.onclose(() => {
        if (!valid()) return;
        current.retired = true; subscriptions?.dispose(); subscriptions = undefined; retry();
      });
      await current.connection.start();
      if (!valid()) { await cleanup(current); return; }
      if (!current.sessionId) throw new Error('MISSING_REALTIME_SESSION');
      status('subscribing');
      subscriptions = createSubscriptionManager({ sessionId: current.sessionId, send: gateway.subscribe,
        onSynced: () => { if (valid()) { coordinator.catchUp(); status('subscribed'); } },
        onError: error => {
          if (!valid()) return;
          status(error.status === 403 ? 'access-unavailable' : error.status === 401 ? 'sign-in-required' : 'subscription-failed');
          if ([401, 403, 404].includes(error.status)) {
            subscriptions?.dispose(); subscriptions = undefined;
            void cleanup(current);
            if (error.status === 404) retry();
          }
        } });
      subscriptions.setResources(desired);
    } catch (error) {
      if (!valid()) return;
      await cleanup(current);
      if (!active || disposed || id !== generation) return;
      const code = error.status || current.httpStatus;
      if (code === 401 || code === 403) status(code === 401 ? 'sign-in-required' : 'access-unavailable');
      else retry();
    } finally { connecting = false; }
  }
  return {
    start() {
      if (!config.enabled || active || disposed) return;
      active = true; coordinator.setEnabled(true); void connect();
    },
    setResources(resources) { desired = resources; if (active) subscriptions?.setResources(resources); },
    resume() { if (active) { if (subscriptions) subscriptions.renew(); else { clearTimeout(timer); void connect(); } } },
    dispose() {
      if (disposed) return;
      const wasActive = active;
      disposed = true; active = false; generation++; clearTimeout(timer); subscriptions?.dispose();
      if (wasActive) coordinator.setEnabled(false);
      void cleanup(attempt);
    },
  };
}
